import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { useNotification } from "../../NotificationContext";
import { createEpisode } from "../../apis/episode";
import { getEpisodeTypes } from "../../apis/episodeType";

interface CreateEpisodeModalProps {
  patientId: number;
  patientName?: string;
  onClose: () => void;
  onCreated?: () => void;
}

const CreateEpisodeModal: React.FC<CreateEpisodeModalProps> = ({
  patientId,
  patientName,
  onClose,
  onCreated,
}) => {
  const { showNotification } = useNotification();
  const [episodeTypes, setEpisodeTypes] = useState<any[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [meta, setMeta] = useState<Record<string, any>>({});
  const [customMetaItems, setCustomMetaItems] = useState<
    Array<{ key: string; value: string }>
  >([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getEpisodeTypes({ per_page: 100 }).then((res: any) => {
      setEpisodeTypes(res.data?.data || res.data || []);
    });
  }, []);

  const selectedType = useMemo(
    () => episodeTypes.find((type) => type.id === selectedTypeId),
    [episodeTypes, selectedTypeId]
  );

  const metaFields = useMemo(() => {
    if (!selectedType) return [];
    if (Array.isArray(selectedType.metaFields)) {
      return selectedType.metaFields;
    }
    if (Array.isArray(selectedType.meta?.fields)) {
      return selectedType.meta.fields;
    }
    if (Array.isArray(selectedType.meta)) {
      return selectedType.meta;
    }
    return [];
  }, [selectedType]);

  useEffect(() => {
    if (metaFields.length > 0) {
      const defaults: Record<string, any> = {};
      metaFields.forEach((field: any) => {
        if (field.key) {
          defaults[field.key] =
            field.defaultValue ?? field.default ?? "";
        }
      });
      setMeta(defaults);
    } else {
      setMeta({});
    }
    setCustomMetaItems([]);
  }, [selectedTypeId, metaFields.length]);

  const updateMetaField = (key: string, value: any) => {
    setMeta((prev) => ({ ...prev, [key]: value }));
  };

  const addCustomMeta = () => {
    setCustomMetaItems((prev) => [...prev, { key: "", value: "" }]);
  };

  const updateCustomMeta = (index: number, field: "key" | "value", value: string) => {
    setCustomMetaItems((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      )
    );
  };

  const removeCustomMeta = (index: number) => {
    setCustomMetaItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSave = async () => {
    if (!selectedTypeId) {
      showNotification("warning", "Please select an episode type", "", "topRight");
      return;
    }
    const metaPayload: Record<string, any> = { ...meta };
    if (customMetaItems.length > 0) {
      customMetaItems.forEach((item) => {
        if (item.key.trim()) {
          metaPayload[item.key.trim()] = item.value;
        }
      });
    }
    setIsSaving(true);
    try {
      await createEpisode({
        patientId,
        episodeTypeId: selectedTypeId,
        startDate,
        meta: metaPayload,
      });
      showNotification("success", "Episode created", "", "topRight");
      onClose();
      onCreated?.();
    } catch (error: any) {
      showNotification(
        "error",
        "Failed to create episode",
        error?.response?.data?.message || error?.message || "",
        "topRight"
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm text-gray-600">Patient</div>
        <div className="text-sm font-semibold text-gray-900">
          {patientName || `Patient #${patientId}`}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Episode Type
        </label>
        <Select
          style={{ width: "100%" }}
          value={selectedTypeId ?? undefined}
          onChange={(value) => setSelectedTypeId(value)}
          options={episodeTypes.map((type) => ({
            value: type.id,
            label: type.name,
          }))}
          placeholder="Select episode type"
        />
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium text-gray-700">Details</div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
        {metaFields.length > 0 ? (
          <>
          {metaFields.map((field: any) => {
            const key = field.key || field.name;
            const label = field.label || field.name || field.key;
            const type = field.type || "text";
            const value = meta[key] ?? "";
            if (!key) return null;
            if (type === "textarea") {
              return (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700">
                    {label}
                  </label>
                  <textarea
                    value={value}
                    onChange={(e) => updateMetaField(key, e.target.value)}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
              );
            }
            if (type === "select" && Array.isArray(field.options)) {
              const options = field.options.map((opt: any) => ({
                value: opt.value ?? opt,
                label: opt.label ?? opt,
              }));
              return (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700">
                    {label}
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    value={value || undefined}
                    onChange={(val) => updateMetaField(key, val)}
                    options={options}
                  />
                </div>
              );
            }
            return (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700">
                  {label}
                </label>
                <input
                  type={type === "number" ? "number" : type === "date" ? "date" : "text"}
                  value={value}
                  onChange={(e) => updateMetaField(key, e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
            );
          })}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-700">
                Additional Info
              </div>
              <button
                type="button"
                onClick={addCustomMeta}
                className="px-3 py-1.5 text-sm font-semibold text-white rounded-md bg-primary hover:bg-hover"
              >
                Add Field
              </button>
            </div>
            {customMetaItems.length === 0 && (
              <div className="text-sm text-gray-500">
                No meta fields configured for this type.
              </div>
            )}
            {customMetaItems.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="text"
                  placeholder="Key"
                  value={item.key}
                  onChange={(e) => updateCustomMeta(index, "key", e.target.value)}
                  className="col-span-5 rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
                <input
                  type="text"
                  placeholder="Value"
                  value={item.value}
                  onChange={(e) => updateCustomMeta(index, "value", e.target.value)}
                  className="col-span-6 rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
                <button
                  type="button"
                  onClick={() => removeCustomMeta(index)}
                  className="col-span-1 text-sm text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isSaving}
          onClick={handleSave}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSaving ? "Creating..." : "Create Episode"}
        </button>
      </div>
    </div>
  );
};

export default CreateEpisodeModal;
